// 最近一次入库记录快照：入库扫描页提交成功后保存，创建仓储容器弹窗里"填入最近入库记录"使用

export const LAST_INBOUND_RECORD_KEY = 'inbound_scan_last_record_v1';

export interface LastInboundRecordItem {
  inventoryUnitCode: string;
  quantity: number;
  sortingGroupSystemCode: string;
}

export interface LastInboundRecordSnapshot {
  recordId: number;
  systemCode?: string;
  submittedAt: string;
  projectCode: string;
  warehouseCode: string;
  items: LastInboundRecordItem[];
}

// 把扫描记录按（分拣组, 库存单元）汇总成快照条目，数量和提交给后端的 inventory_items 同源
export function buildLastInboundRecordSnapshot(params: {
  recordId: number;
  systemCode?: string;
  projectCode: string;
  warehouseCode: string;
  scanHistory: Array<{ inventoryUnitCode: string; quantity: number; sortingGroupSystemCode: string }>;
  submittedAt?: Date;
}): LastInboundRecordSnapshot {
  const merged = new Map<string, LastInboundRecordItem>();
  params.scanHistory.forEach(record => {
    const quantity = Number(record.quantity || 0);
    if (!record.inventoryUnitCode || !(quantity > 0)) return;
    const key = `${record.sortingGroupSystemCode}\u0000${String(record.inventoryUnitCode).toLowerCase()}`;
    const hit = merged.get(key);
    if (hit) {
      hit.quantity += quantity;
    } else {
      merged.set(key, {
        inventoryUnitCode: record.inventoryUnitCode,
        quantity,
        sortingGroupSystemCode: record.sortingGroupSystemCode
      });
    }
  });
  return {
    recordId: params.recordId,
    systemCode: params.systemCode,
    submittedAt: (params.submittedAt || new Date()).toISOString(),
    projectCode: params.projectCode,
    warehouseCode: params.warehouseCode,
    items: Array.from(merged.values())
  };
}

export function saveLastInboundRecord(snapshot: LastInboundRecordSnapshot): void {
  try {
    localStorage.setItem(LAST_INBOUND_RECORD_KEY, JSON.stringify(snapshot));
  } catch (e) {
    console.warn('[LastInboundRecord] 保存失败', e);
  }
}

export function loadLastInboundRecord(): LastInboundRecordSnapshot | null {
  try {
    const raw = localStorage.getItem(LAST_INBOUND_RECORD_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || typeof data.recordId !== 'number' || !Array.isArray(data.items)) return null;
    return data as LastInboundRecordSnapshot;
  } catch {
    return null;
  }
}

export interface FillContainerItem {
  inventoryUnitCode: string;
  inventoryUnitName?: string;
  quantity: number;
  selected: boolean;
  [extra: string]: any;
}

export interface FillRow {
  inventoryUnitCode: string;
  inventoryUnitName?: string;
  recordQuantity: number;
  boardQuantity: number;
  fillQuantity: number;
  note: string;
}

export interface FillPlan<T extends FillContainerItem> {
  items: T[];
  rows: FillRow[];
  // 记录里有、但不在该分拣组看板行中的库存单元编码（已忽略）
  ignoredCodes: string[];
  filledCount: number;
}

// 计算"填入最近入库记录"的结果：
// - 在记录里的单元：勾选，数量 = min(记录数量, 看板现有数量)
// - 不在记录里的单元：取消勾选，数量保持不变
export function planFillFromRecord<T extends FillContainerItem>(params: {
  recordItems: Array<{ inventoryUnitCode: string; quantity: number }>;
  modalItems: T[];
  boardQuantityByCode: Record<string, number>;
}): FillPlan<T> {
  const recordQtyByCode = new Map<string, { code: string; quantity: number }>();
  params.recordItems.forEach(it => {
    const quantity = Number(it.quantity || 0);
    if (!it.inventoryUnitCode || !(quantity > 0)) return;
    const key = String(it.inventoryUnitCode).trim().toLowerCase();
    const hit = recordQtyByCode.get(key);
    if (hit) hit.quantity += quantity;
    else recordQtyByCode.set(key, { code: it.inventoryUnitCode, quantity });
  });
  const boardQty = new Map<string, number>();
  Object.keys(params.boardQuantityByCode).forEach(code => boardQty.set(code.trim().toLowerCase(), Number(params.boardQuantityByCode[code] || 0)));

  const matched = new Set<string>();
  const rows: FillRow[] = [];
  let filledCount = 0;
  const items = params.modalItems.map(item => {
    const key = String(item.inventoryUnitCode).trim().toLowerCase();
    const rec = recordQtyByCode.get(key);
    if (!rec) {
      return { ...item, selected: false };
    }
    matched.add(key);
    const board = boardQty.get(key) ?? 0;
    const fill = Math.min(rec.quantity, board);
    let note = '';
    if (board <= 0) note = '看板上该库存单元没有已分拣数量，未勾选';
    else if (board < rec.quantity) note = `入库 ${rec.quantity} 件，看板仅 ${board} 件，已按 ${board} 件填入`;
    rows.push({
      inventoryUnitCode: item.inventoryUnitCode,
      inventoryUnitName: item.inventoryUnitName,
      recordQuantity: rec.quantity,
      boardQuantity: board,
      fillQuantity: fill,
      note
    });
    if (fill > 0) {
      filledCount += 1;
      return { ...item, selected: true, quantity: fill };
    }
    return { ...item, selected: false };
  });
  const ignoredCodes = Array.from(recordQtyByCode.entries())
    .filter(([key]) => !matched.has(key))
    .map(([, v]) => v.code);
  return { items, rows, ignoredCodes, filledCount };
}

// 后端记录明细按库存单元编码汇总（用于和快照/看板核对）
export function aggregateRecordItems(items: Array<{ code: string; quantity: number }>): Array<{ inventoryUnitCode: string; quantity: number }> {
  const merged = new Map<string, { inventoryUnitCode: string; quantity: number }>();
  items.forEach(it => {
    const quantity = Number(it.quantity || 0);
    if (!it.code || !(quantity > 0)) return;
    const key = it.code.trim().toLowerCase();
    const hit = merged.get(key);
    if (hit) hit.quantity += quantity;
    else merged.set(key, { inventoryUnitCode: it.code, quantity });
  });
  return Array.from(merged.values());
}
